import React, { useState } from 'react';
import { Modal, Button, Avatar, Input } from 'antd';
import { MessageOutlined } from '@ant-design/icons';
import axiosInstance from '../../axios';
import { dateDisplayable } from '../table-utils';

type PaymentCommentsPopupProps = {
    cellValue: number | string;
    data: { payment_id: number; comment_count: string | number }
}

type CommentObjectType = {
    id: number;
    created_at: string;
    comment: string;
    admin_name: string;
};

function PaymentCommentsPopup(props: PaymentCommentsPopupProps) {
    const { payment_id, comment_count } = props?.data;
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [commentsArray, setCommentsArray] = useState<CommentObjectType[]>([]);
    const [newComment, setNewComment] = useState('');
    const [loading, setLoading] = useState(false);

    const showModal = async () => {
        setLoading(true);
        await fetchCommentsArray();
        setIsModalVisible(true);
    };

    const handleOk = () => {
        setIsModalVisible(false);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
    };

    async function fetchCommentsArray() {
        try {
            const { data } = await axiosInstance.get(`/v1/cre/student/payment/comments/${payment_id}`);
            console.log('The response data is ', data.data);
            setCommentsArray(data.data);
        } catch (error) {
            console.error('Error fetching payment comments:', error);
        } finally {
            setLoading(false);
        }
    }

    async function postNewComment(newComment: string) {
        if (!newComment) return;
        try {
            setLoading(true);
            const response = await axiosInstance.post(`/v1/cre/student/payment/comment`, {
                paymentId: payment_id,
                comment: newComment
            });
            console.log('Payment comment posted successfully:', response.data);
            if (response.data?.success) {
                // Clear input
                setNewComment('');

                // Refresh comment list
                await fetchCommentsArray();

                // Increment comment count locally
                props.data.comment_count = Number(props.data.comment_count) + 1;
            }
        } catch (error) {
            console.error('Error posting payment comment:', error);
        } finally {
            setLoading(false);
        }
    }
    
    return (
        <div>
            <Button
                className="tail-mx-auto"
                type="primary"
                onClick={showModal}
                size="small"
                icon={<MessageOutlined />}
            >
                {comment_count}
            </Button>
            <Modal
                loading={loading}
                title="Payment Comments"
                open={isModalVisible}
                onOk={handleOk}
                onCancel={handleCancel}
                footer={[
                    <Button key="close" onClick={handleCancel}>
                        Cancel
                    </Button>,
                    <Button key="ok" type='primary' onClick={() => postNewComment(newComment)}>
                        Ok
                    </Button>,
                ]}
            >
                <div className='tail-flex tail-flex-col tail-max-h-[400px] tail-gap-y-4 tail-overflow-y-scroll tail-mb-4'>
                    {commentsArray.map((comment) => (
                        <div key={comment.id} className='tail-flex tail-gap-x-4 tail-border-b last:tail-border-none tail-pb-4'>
                            <Avatar />
                            <div className='tail-grow'>
                                <div className='tail-flex tail-justify-between tail-w-full'>
                                    <h6>{comment.admin_name}</h6>
                                    <h6>{dateDisplayable(comment.created_at)}</h6>
                                </div>
                                <div>
                                    <p>{comment.comment}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                <div>
                    <Input
                        placeholder="Write a payment comment..."
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                    />
                </div>
            </Modal>
        </div>
    );
}

export default PaymentCommentsPopup;
